import React, { useId } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from './Icon';

/**
 * Shared Select component — styled native <select> with a custom chevron and
 * optional form-field wrapper (label / hint / error).
 *
 * @param {string} [label]         - Renders a form-field label above the select
 * @param {string} [hint]          - Helper text below (hidden when error is set)
 * @param {string} [error]         - Error text below; switches border/ring to error tokens
 * @param {boolean} [required]     - Appends a visible " *" to the label and sets native required
 * @param {string} [placeholder]   - Renders a disabled first option with this text
 * @param {'pill'|'sm'|'md'|'lg'|null} [size] - Padding density (default 'md'). 'pill'
 *   is the Content Filter Pills density from the style guide (px-3 py-1.5
 *   text-xs) — use it with shape="pill" for a one-of-N filter sitting in a row
 *   of filter chips, where an input-density control reads as a different class
 *   of thing entirely. Pass null to opt out and supply padding via className.
 * @param {'default'|'pill'} [shape] - Corner radius. 'default' is rounded-lg,
 *   the Input Fields shape. 'pill' is rounded-full, so a filter dropdown
 *   matches the rounded-full chips beside it. A prop rather than a className
 *   override on purpose: rounded-lg is in this component's base classes, and
 *   Tailwind resolves competing utilities by stylesheet order rather than by
 *   the order they appear in a class attribute — so overriding from the call
 *   site works only by accident of the theme's scale order.
 * @param {string} [className]     - Additional classes on the <select> element
 * @param {string} [wrapperClassName] - Additional classes on the outer wrapper
 * All other props are passed through to the native <select>.
 */

const SIZE_CLASSES = {
  // Matches the style guide's Content Filter Pills, so a filter dropdown can
  // sit in a chip row without being a third density.
  pill: 'px-3 py-1.5 text-xs',
  sm: 'px-2.5 py-1.5 text-sm',
  md: 'px-3 py-2 text-sm',
  lg: 'px-4 py-3',
};

const SHAPE_CLASSES = {
  default: 'rounded-lg',
  pill: 'rounded-full',
};

const CHEVRON_POSITION = {
  pill: 'w-3.5 h-3.5 right-2',
  sm: 'w-3.5 h-3.5 right-2',
  md: 'w-4 h-4 right-3',
  lg: 'w-4 h-4 right-3',
};

const Select = React.forwardRef(
  (
    {
      label,
      hint,
      error,
      required = false,
      placeholder,
      size = 'md',
      shape = 'default',
      className = '',
      wrapperClassName = '',
      id: idProp,
      value,
      children,
      ...props
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const generatedId = useId();
    const id = idProp || `select-${generatedId}`;
    const sizeClasses = size === null ? '' : SIZE_CLASSES[size] || SIZE_CLASSES.md;
    const shapeClasses = SHAPE_CLASSES[shape] || SHAPE_CLASSES.default;
    const chevronClasses = CHEVRON_POSITION[size] || CHEVRON_POSITION.md;

    const errorId = error ? `${id}-error` : undefined;
    const hintId = hint && !error ? `${id}-hint` : undefined;
    const describedBy = errorId || hintId;

    const borderClasses = error
      ? 'border-error focus-visible:ring-error'
      : 'border-border focus-visible:ring-primary-light';

    const selectClasses = [
      'w-full appearance-none cursor-pointer',
      shapeClasses,
      'bg-surface text-content-primary',
      'border',
      borderClasses,
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
      'transition-colors duration-fast',
      'disabled:opacity-60 disabled:cursor-not-allowed',
      'pr-8', // room for the chevron
      sizeClasses,
      className,
    ]
      .filter(Boolean)
      .join(' ');

    const hasPlaceholder = placeholder !== undefined && placeholder !== null;
    const isPlaceholderSelected =
      hasPlaceholder && (value === '' || value === undefined || value === null);

    const selectEl = (
      <div className="relative">
        <select
          ref={ref}
          id={id}
          value={value}
          required={required}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          className={selectClasses}
          {...props}
        >
          {hasPlaceholder && (
            <option value="" disabled hidden={!isPlaceholderSelected}>
              {placeholder}
            </option>
          )}
          {children}
        </select>
        <Icon
          name="chevronDown"
          className={`absolute top-1/2 -translate-y-1/2 ${chevronClasses} text-content-muted pointer-events-none`}
        />
      </div>
    );

    // No form-field affordances requested — return just the select element.
    if (!label && !hint && !error) {
      return wrapperClassName ? <div className={wrapperClassName}>{selectEl}</div> : selectEl;
    }

    return (
      <div className={wrapperClassName}>
        {label && (
          <label
            htmlFor={id}
            className="block text-sm font-medium text-content-secondary mb-1"
          >
            {label}
            {required && (
              <span className="text-error ml-0.5" aria-hidden="true">
                {t('select.requiredIndicator')}
              </span>
            )}
          </label>
        )}
        {selectEl}
        {error && (
          <p id={errorId} role="alert" className="mt-1 text-xs text-error">
            {error}
          </p>
        )}
        {hint && !error && (
          <p id={hintId} className="mt-1 text-xs text-content-muted">
            {hint}
          </p>
        )}
      </div>
    );
  },
);

Select.displayName = 'Select';

export default Select;
